import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"

import { Children } from "../../../../../../../../../components/miscellianous/children"
import { BSLevel } from "../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/BSLevel"
import { BSLevelForm } from "../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/BSLevelForm"
import { useBSLevel } from "../../../../../../../../../core/hooks/beem-shot/useBSLevel"
import { BSBuildingContext } from "../../../components/context/BSBuildingContext"
import { GroupLevel } from "./GroupLevel"

export function sortGroupLevelByLevelNumber(
  groupLevelListWithoutOldGroup: GroupLevel[],
  ungroupGroupList?: GroupLevel[]
): GroupLevel[] {
  return ungroupGroupList
    ? [...groupLevelListWithoutOldGroup, ...ungroupGroupList].sort(
        (a, b) => b.levelList[0].levelNumber - a.levelList[0].levelNumber
      )
    : groupLevelListWithoutOldGroup.sort((a, b) => b.levelList[0].levelNumber - a.levelList[0].levelNumber)
}

function sortGroupLevelByGroupNumber(groupLevelList: GroupLevel[]): GroupLevel[] {
  return groupLevelList.sort((a, b) => b.groupNumber - a.groupNumber)
}

export const BSLevelContext = React.createContext<BSBuildingLevelStore>({} as BSBuildingLevelStore)

export function BSLevelContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { getBSLevelList } = useBSLevel()

  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const [groupLevelSuperstructureList, setGroupLevelSuperstructureList] = useState<GroupLevel[]>([])
  const [groupLevelInfrastructureList, setGroupLevelInfrastructureList] = useState<GroupLevel[]>([])
  const [isLevelUpdated, setIsLevelUpdated] = useState<boolean>(false)
  const [hasToControlPatio, setHasToControlPatio] = useState<boolean>(false)

  function bsBuildingLevelDtoToForm(dto: BSLevel): BSLevelForm {
    return {
      id: dto.id,
      bsParkingList: dto.bsParkingList,
      height: dto.height,
      levelNumber: dto.levelNumber,
      superstructure: dto.superstructure,
      groupId: dto.groupId,
      groupNumber: dto.groupNumber,
      zonePercentage: dto.zonePercentage,
    }
  }

  function addToMap(groups: Map<string, GroupLevel>, level: BSLevelForm): void {
    let group = groups.get(level.groupId)
    if (!group) {
      group = {
        id: level.groupId,
        groupNumber: level.groupNumber,
        levelList: [],
      }
      groups.set(level.groupId, group)
    }
    group.levelList.push(level)
  }

  const setLevelGroups: (bsBuildingLevelList: BSLevel[]) => void = useCallback((bsBuildingLevelList) => {
    const superstructureLevelGroups = new Map<string, GroupLevel>()
    const infrastructureLevelGroups = new Map<string, GroupLevel>()

    bsBuildingLevelList.forEach((level) => {
      const levelForm = bsBuildingLevelDtoToForm(level)
      if (level.superstructure) {
        addToMap(superstructureLevelGroups, levelForm)
      } else {
        addToMap(infrastructureLevelGroups, levelForm)
      }
    })

    setGroupLevelSuperstructureList(sortGroupLevelByGroupNumber(Array.from(superstructureLevelGroups.values())))
    setGroupLevelInfrastructureList(sortGroupLevelByGroupNumber(Array.from(infrastructureLevelGroups.values())))
  }, [])

  const refreshBuildingLevel: () => Promise<void> = useCallback(() => {
    setIsLevelUpdated(false)
    if (selectedBSBuilding?.id) {
      return getBSLevelList(selectedBSBuilding.id).then((bsBuildingLevelList) => {
        setLevelGroups(bsBuildingLevelList)
      })
    } else {
      setGroupLevelSuperstructureList([])
      setGroupLevelInfrastructureList([])
      return Promise.resolve()
    }
  }, [getBSLevelList, selectedBSBuilding, setLevelGroups])

  useEffect(() => {
    refreshBuildingLevel()
  }, [refreshBuildingLevel])

  const groupLevelStore: BSBuildingLevelStore = useMemo(
    () => ({
      groupLevelSuperstructureList,
      groupLevelInfrastructureList,
      isLevelRefreshing: isLevelUpdated,
      hasToControlPatio,
      setIsLevelUpdated,
      setHasToControlPatio,
      setLevelGroups,
      refreshBuildingLevel,
    }),
    [
      groupLevelSuperstructureList,
      groupLevelInfrastructureList,
      isLevelUpdated,
      hasToControlPatio,
      setIsLevelUpdated,
      setHasToControlPatio,
      setLevelGroups,
      refreshBuildingLevel,
    ]
  )
  return <BSLevelContext.Provider value={groupLevelStore}>{children}</BSLevelContext.Provider>
}

export interface BSBuildingLevelStore {
  groupLevelSuperstructureList: GroupLevel[]
  groupLevelInfrastructureList: GroupLevel[]
  isLevelRefreshing: boolean
  hasToControlPatio: boolean
  setIsLevelUpdated: React.Dispatch<React.SetStateAction<boolean>>
  setHasToControlPatio: React.Dispatch<React.SetStateAction<boolean>>
  setLevelGroups(bsBuildingLevelList: BSLevel[]): void
  refreshBuildingLevel(): Promise<void>
}
